import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "..";
import { EntityId } from "@reduxjs/toolkit";
import { EnqueueFetcher } from "../Thunks/enqueueFetcher";
import { useEffect, useMemo } from "react";
import { isEqual } from "lodash";

type EnqueueFetchByIds<T, Id extends EntityId> = InstanceType<typeof EnqueueFetcher<T, Id>>["enqueueFetchByIds"]

export function entitiesByMultipleIdsWithStatusHookFactory<T, Id extends EntityId>(
  fetchThunk: EnqueueFetchByIds<T, Id>,
  selector: (state: RootState, ids: Id[]) => Record<Id, T>,
  loadingSelector: (state: RootState, ids: Id[]) => Record<Id, boolean>,
): (ids?: Id[]) => (Record<Id, { entity: T | undefined, isLoading: boolean }>) {

  return (ids?: Id[]) => {

    const dispatch = useAppDispatch();

    const entities: Record<Id, T> | undefined = useSelector((state: RootState) => { if (ids && ids.length) { return selector(state, ids) } }, isEqual);

    const isLoading = useSelector((state: RootState) => {
      if (ids && ids.length) {
        return loadingSelector(state, ids);
      }
      return ids?.reduce((agg, id) => {
        agg[id] = true;
        return agg;
      }, {} as Record<Id, boolean>);
    }, isEqual
    );

    useEffect(() => {
      if (ids && ids.length) {
        dispatch(fetchThunk(ids));
      }
    }, [dispatch, ids])

    const record = useMemo(() => ids?.reduce((agg, id) => {
      agg[id] = { entity: entities?.[id], isLoading: isLoading?.[id] ?? true }
      return agg;
    }, {} as Record<Id, { entity: T | undefined, isLoading: boolean }>), [entities, ids, isLoading])

    return record ?? {} as Record<Id, { entity: T | undefined, isLoading: boolean }>
  };
}

